// Conversational AnaMoni state.
//
// The store keeps an ordered list of messages — user prompts and assistant
// responses (filter, ambiguity banner, error, or out-of-domain). Messages
// stay in memory only. Multi-turn
// refinement uses `lastFilter` (tracked separately so out-of-domain or
// ambiguity turns don't reset the working filter).
//
// Designed to be reusable: the message shape is generic enough that
// future INNODIA chat surfaces (CTS, planner-driven workflows) can mount
// a similar primitive on the same Zustand pattern.
import { create } from "zustand";
import type { AnaMoniResponse, FilterGroup } from "../types/inpact";

export type Role = "user" | "assistant";

export interface UserMessage {
  role: "user";
  id: string;
  ts: number;
  text: string;
}

export interface AssistantMessage {
  role: "assistant";
  id: string;
  ts: number;
  // Discriminated union over what the planner produced. Drives the bubble
  // body (filter summary, clarification banner, out-of-domain explainer,
  // or error chrome). `text` is a one-line tldr suitable for screen readers.
  response: AnaMoniResponse | { kind: "error"; message: string };
  text: string;
}

export type ChatMessage = UserMessage | AssistantMessage;

interface AnaMoniState {
  messages: ChatMessage[];
  pending: boolean;
  /** Last successful filter — passed back to the planner as
   *  `prior_filter_group` on the next user turn (multi-turn refinement). */
  lastFilter: FilterGroup | null;

  appendUser: (text: string) => string;
  appendAssistant: (response: AssistantMessage["response"], text: string) => void;
  setPending: (p: boolean) => void;
  setLastFilter: (f: FilterGroup | null) => void;
  clear: () => void;
}

const MAX_HISTORY = 50;

// Migration cleanup for versions that persisted raw prompts and responses.
try {
  window.localStorage.removeItem("inpact:anamoni-history");
} catch {
  // Storage can be disabled; an absent cleanup target is already safe.
}

let _idCounter = 0;
function newId(): string {
  _idCounter += 1;
  return `${Date.now().toString(36)}-${_idCounter.toString(36)}`;
}

export const useAnaMoniStore = create<AnaMoniState>()(
    (set) => ({
      messages: [],
      pending: false,
      lastFilter: null,

      appendUser: (text) => {
        const id = newId();
        set((s) => ({
          messages: [
            ...s.messages.slice(-MAX_HISTORY + 1),
            { role: "user", id, ts: Date.now(), text },
          ],
        }));
        return id;
      },

      appendAssistant: (response, text) =>
        set((s) => ({
          messages: [
            ...s.messages.slice(-MAX_HISTORY + 1),
            { role: "assistant", id: newId(), ts: Date.now(), response, text },
          ],
        })),

      setPending: (pending) => set({ pending }),
      setLastFilter: (lastFilter) => set({ lastFilter }),
      clear: () => set({ messages: [], lastFilter: null }),
    }),
);
